import { useState, useEffect, useContext } from 'react';
import {
    Container,
    Box,
    Typography,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import { format, parseISO, isAfter, isBefore } from 'date-fns';
import ModalImage from 'react-modal-image';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '../../../lib/dateLocale';
import { useCrud } from '../../../lib/hooks';
import { AppContext, SttTabs } from '../../../sporttia/all';
import RateList from './RateList';
import ClassesList from './ClassesList';
import ModalGotoSc from './ModalGotoSc';
import ModalSignUp from './ModalSignUp';
import SignedUsersList from '../../../sporttia/lists/SignedUsersList';
import constants from '../../../config/constants';

export default function Activity() {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const { id } = useParams();

    const [activity, setActivity, , Get] = useCrud<any>();
    const [classes, setClasses] = useState({ count: 0, rows: [] });
    const [rateModalGoto, setRateModalGoto] = useState(null);
    const [rateModalSignup, setRateModalSignup] = useState(null);
    const [privileges, setPrivileges] = useState(null);
    const [amSignedUp, setAmSignedUp] = useState(null);
    const [idMship, setIdMship] = useState<any>();
    const [files, setFiles] = useState([]);

    const theme = useTheme();
    const smDown = useMediaQuery(theme.breakpoints.down('sm'));

    function loadClasses(params = { rows: 20, page: 1 }) {
        Get(`/activities/${id}/classes`, params).then((response: any) => {
            if (response.rows) {
                setClasses(response);
            }
        });
    }

    function loadMyInscription($idStudent: number) {
        if (!$idStudent) {
            return;
        }

        Get(`/students/${$idStudent}`).then((response: any) => {
            if (response.student) {
                setAmSignedUp(response.student);
            }
        });
    }

    function loadActivity() {
        Get(`/activities/${id}`).then((response: any) => {
            if (response.activity) {
                setActivity(response.activity);

                response.activity.rates?.forEach((rate: any) => {
                    if (rate.myStudent) {
                        loadMyInscription(rate.myStudent.id);
                    }
                });
            }
        });
    }

    function loadActivityFiles() {
        Get(`/activities/${id}/files`).then((response: any) => {
            if (response.count > 0) {
                setFiles(response.rows);
            }
        });
    }

    function loadMe() {
        Get(`/scs/${activity.sc.id}/me`)
            .then((response: any) => {
                if (response.mship) {
                    setPrivileges(response.mship.privileges);
                    setIdMship(response.mship.id);
                }
            })
            .catch(() => {});
    }

    useEffect(() => {
        if (id) {
            loadActivity();
            loadClasses();
            loadActivityFiles();
        }
    }, []);

    useEffect(() => {
        if (
            cxt.logged &&
            cxt.user?.role !== 'SPORTCENTER' &&
            activity &&
            activity.sc
        ) {
            loadMe();
        }
    }, [activity]);

    // Open signup modal
    function signUp(rate: any) {
        if (rate.signupOnline) {
            setRateModalSignup(rate);
        } else {
            setRateModalGoto(rate);
        }
    }

    if (!activity) {
        return null;
    }

    const now = new Date();
    const inTime =
        isAfter(now, parseISO(activity.ini)) &&
        isBefore(now, parseISO(activity.end));
    const inIscTime =
        isAfter(now, parseISO(activity.inscIni)) &&
        isBefore(now, parseISO(activity.inscEnd));

    return (
        <Container maxWidth="md">
            <Box display="flex" flexDirection={smDown ? 'column' : 'row'}>
                <Box>
                    <Box position="relative">
                        <Box
                            position="absolute"
                            left={0}
                            top={0}
                            p={1}
                            pb={3}
                            sx={{
                                background:
                                    'linear-gradient(180deg, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)',
                                color: 'white',
                                width: '100%',
                            }}
                        >
                            <Typography>{activity.name}</Typography>
                        </Box>
                        {activity.poster?.id ? (
                            <ModalImage
                                hideZoom
                                small={`${activity.poster.url}?outfit=282`}
                                large={`${activity.poster.url}?outfit=800`}
                                alt={activity.name}
                            />
                        ) : (
                            <img
                                alt="Event poster"
                                style={{
                                    width: smDown ? '100%' : '282px',
                                    height: 'auto',
                                    borderRadius: 4,
                                }}
                                src={`${cxt.apiEnv.url}/v6/public/images/eventPosters/event_default_68.jpg`}
                            />
                        )}
                    </Box>
                    <Box mt={2}>
                        <Box
                            p={1}
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            sx={{
                                backgroundColor: inIscTime
                                    ? '#4caf50'
                                    : '#f44336',
                                color: 'white',
                                borderRadius: 1,
                            }}
                        >
                            <Typography
                                variant="caption"
                                sx={{ fontWeight: 'bold' }}
                            >
                                {inIscTime
                                    ? t('activity.inscInTime')
                                    : t('activity.inscOutTime')}
                            </Typography>
                            <Typography variant="body1">{`${format(
                                parseISO(activity.inscIni),
                                'd MMM yyyy HH:mm:ss',
                                { locale: getDateLocale() },
                            )} - ${format(
                                parseISO(activity.inscEnd),
                                'd MMM yyyy HH:mm:ss',
                                { locale: getDateLocale() },
                            )}`}</Typography>
                        </Box>
                    </Box>
                    <Box mt={2}>
                        <Box
                            p={1}
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            sx={{
                                backgroundColor: inTime ? '#4caf50' : '#f44336',
                                color: 'white',
                                borderRadius: 1,
                            }}
                        >
                            <Typography
                                variant="caption"
                                sx={{ fontWeight: 'bold' }}
                            >
                                {inTime
                                    ? t('activity.inTime')
                                    : t('activity.outTime')}
                            </Typography>
                            <Typography variant="body1">{`${format(
                                parseISO(activity.ini),
                                'd MMM yyyy',
                                { locale: getDateLocale() },
                            )} - ${format(
                                parseISO(activity.end),
                                'd MMM yyyy',
                                { locale: getDateLocale() },
                            )}`}</Typography>
                        </Box>
                    </Box>
                    {files.length > 0 && (
                        <Box mb={3}>
                            <Box mt={3} mb={2}>
                                <Typography variant="body1">
                                    <b>{t('general.documents')}:</b>
                                </Typography>
                            </Box>
                            {files.map((file: any) => (
                                <Box my={1} key={file.id}>
                                    <div>
                                        <a href={file.url}>{file.name}</a>
                                    </div>
                                    <Typography variant="body2">
                                        {file.description}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    )}
                </Box>
                <Box p={3} flex={1}>
                    {/* --- Description --- */}
                    <Box mb={2}>
                        <div
                            dangerouslySetInnerHTML={{
                                __html: activity.description,
                            }}
                        />
                    </Box>

                    {amSignedUp && (
                        <Box
                            my={1}
                            sx={{
                                backgroundColor: '#e3f2fd',
                                color: '#1976d2',
                                padding: 1,
                                borderRadius: 1,
                            }}
                        >
                            <Typography>{t('activity.imSigned')}</Typography>
                        </Box>
                    )}
                    <SttTabs
                        tabs={[
                            {
                                show:
                                    activity?.status !==
                                    constants.activity.status.closed,
                                caption: t('activity.title'),
                                component: (
                                    <RateList
                                        rates={activity.rates}
                                        onSelected={signUp}
                                        inIscTime={inIscTime}
                                    />
                                ),
                            },
                            {
                                caption: t('activity.calendar'),
                                show: classes.count > 0,
                                component: (
                                    <ClassesList
                                        classes={classes}
                                        onFetch={loadClasses}
                                    />
                                ),
                            },
                            {
                                caption: t('activity.signedUsers'),
                                show:
                                    activity.rates?.filter(
                                        (rate: any) =>
                                            rate?.signedUsers?.length > 0,
                                    )?.length > 0,
                                component: (
                                    <SignedUsersList
                                        items={(activity.rates ?? [])
                                            .map(
                                                (rate: any) =>
                                                    rate?.signedUsers,
                                            )
                                            .flat()}
                                    />
                                ),
                            },
                        ]}
                    />
                </Box>
            </Box>
            {rateModalGoto && (
                <ModalGotoSc
                    rate={rateModalGoto}
                    onClose={() => setRateModalGoto(null)}
                    scContactInfo={{
                        phone: activity.sc.phonePublic,
                        email: activity.sc.emailPublic,
                    }}
                />
            )}
            {rateModalSignup && (
                <ModalSignUp
                    rate={rateModalSignup}
                    idSC={activity.sc.id}
                    onClose={() => {
                        setRateModalSignup(null);
                    }}
                    form={activity.form}
                    privileges={privileges}
                    paymentForms={activity.paymentForms}
                    idMship={idMship}
                />
            )}
        </Container>
    );
}
